/**
 * Checking your microphone before anybody hears it.
 *
 * The usual way to find out that the wrong microphone is selected, or that
 * the browser never got permission, is to join a call and be told by four
 * other people that you are silent. This asks first: pick the device, watch
 * the meter move while you talk, and only then join.
 *
 * The meter is a real reading off the live stream, not a decoration - if it
 * does not move, the microphone genuinely is not picking you up, and the
 * panel says what to do about it.
 */

import { useEffect, useRef, useState } from 'react';
import { listDevices, useVoice } from '@/stores/voice';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, Field, Modal } from './ui';
import { Select } from './Select';

const SKIP_KEY = 'pogu.mic.skip';

/** Whether the check should be offered before joining voice. */
export function micCheckWanted(): boolean {
  try { return localStorage.getItem(SKIP_KEY) !== '1'; } catch { return true; }
}

type Trouble = 'denied' | 'missing' | 'failed' | null;

export function MicCheck({ onClose, onJoin, title, joinLabel }: {
  onClose: () => void;
  /** Given when the person is happy with what they heard. Omitted in settings. */
  onJoin?: () => void;
  title?: string;
  joinLabel?: string;
}) {
  const voice = useVoice();
  const settings = useSession((s) => s.settings);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [inputId, setInputId] = useState(voice.inputId);
  const [level, setLevel] = useState(0);
  const [peak, setPeak] = useState(0);
  const [trouble, setTrouble] = useState<Trouble>(null);
  const [skip, setSkip] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const frameRef = useRef(0);

  /* One live stream at a time, torn down on every change and on close - a
     microphone left open is a light on somebody's webcam. */
  useEffect(() => {
    let dead = false;

    async function start() {
      stop();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: settings?.voiceEcho !== false,
            noiseSuppression: settings?.voiceNoise !== false,
            autoGainControl: settings?.voiceAgc !== false,
            ...(inputId ? { deviceId: { exact: inputId } } : null)
          },
          video: false
        });
        if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        setTrouble(null);

        const ctx = new AudioContext();
        ctxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);

        const read = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (const v of data) {
            const x = (v - 128) / 128;
            sum += x * x;
          }
          /* Root mean square, scaled so ordinary speech lands around half. */
          const rms = Math.sqrt(sum / data.length);
          const shown = Math.min(1, rms * 3.2);
          setLevel(shown);
          setPeak((p) => Math.max(shown, p * 0.97));
          frameRef.current = requestAnimationFrame(read);
        };
        read();

        /* Names only arrive once permission is granted. */
        const list = await listDevices();
        if (!dead) setDevices(list.inputs);
      } catch (err) {
        const name = (err as DOMException)?.name;
        setTrouble(name === 'NotAllowedError' ? 'denied' : name === 'NotFoundError' ? 'missing' : 'failed');
      }
    }

    function stop() {
      cancelAnimationFrame(frameRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      void ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
    }

    void start();
    return () => { dead = true; stop(); };
  }, [inputId, settings?.voiceEcho, settings?.voiceNoise, settings?.voiceAgc]);

  function finish(join: boolean) {
    try { localStorage.setItem(SKIP_KEY, skip ? '1' : '0'); } catch { /* storage off */ }
    if (voice.inputId !== inputId) void voice.setInput(inputId);
    if (join) onJoin?.();
    onClose();
  }

  const heard = peak > 0.06;

  return (
    <Modal
      title={title ?? 'Check your microphone'}
      subtitle="Say something. The bar should move."
      onClose={onClose}
      footer={
        onJoin ? (
          <>
            <Button kind="outline" onClick={onClose}>Cancel</Button>
            <Button data-primary kind="primary" onClick={() => finish(true)}>{joinLabel ?? 'Join voice'}</Button>
          </>
        ) : (
          <Button data-primary kind="primary" onClick={() => finish(false)}>Done</Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Microphone">
          <Select
            value={inputId}
            onChange={setInputId}
            options={[
              { value: '', label: 'System default' },
              ...devices.map((d, i) => ({ value: d.deviceId, label: d.label || 'Microphone ' + (i + 1) }))
            ]}
          />
        </Field>

        {/* The meter. Segments rather than a smooth bar: it is much easier to
            see movement in a row of blocks than in a sliding edge. */}
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <Icon name={heard ? 'mic' : 'micOff'} size={15} className={heard ? 'text-good' : 'text-mute'} />
            <span className={cn('text-[13px] font-semibold', heard ? 'text-good' : 'text-mute')}>
              {trouble ? 'Nothing is coming through' : heard ? 'Hearing you' : 'Say something…'}
            </span>
          </div>
          <div className="flex h-5 items-stretch gap-[3px]" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)} aria-label="Microphone level">
            {Array.from({ length: 24 }, (_, i) => {
              const lit = level * 24 > i;
              const hot = i > 18;
              return (
                <span
                  key={i}
                  className={cn(
                    'flex-1 rounded-[2px] transition-[background-color] duration-[80ms]',
                    lit ? (hot ? 'bg-warn' : 'bg-accent') : 'bg-surface-3'
                  )}
                />
              );
            })}
          </div>
        </div>

        {trouble ? (
          <div className="rounded-[calc(12px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--bad)_8%,var(--bg-1))] p-3 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_30%,transparent)]">
            <p className="m-0 text-[13.5px] font-semibold text-ink">
              {trouble === 'denied' ? 'Pugo is not allowed to use your microphone'
                : trouble === 'missing' ? 'No microphone was found'
                  : 'The microphone could not be opened'}
            </p>
            <p className="m-0 mt-1 text-[12.5px] text-mute">
              {trouble === 'denied'
                ? 'Allow the microphone for this app, then open this panel again. On Windows, check Settings › Privacy › Microphone as well.'
                : trouble === 'missing'
                  ? 'Plug one in, or choose a different device above.'
                  : 'Another program may be holding it. Close it and try again.'}
            </p>
          </div>
        ) : null}

        {onJoin ? (
          <label className="flex items-center gap-2.5 text-[13px] text-dim">
            <input
              type="checkbox"
              checked={skip}
              onChange={(e) => setSkip(e.target.checked)}
              className="h-4 w-4 accent-[var(--accent)]"
            />
            Do not check again before joining
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
